/**
 * BillingAccountForm - Core Stripe payment form for subscription creation and card updates.
 *
 * This is the main payment component handling all Stripe integration for the billing
 * system. It manages credit card collection via Stripe Elements and orchestrates the
 * multi-step payment flow with the backend API.
 *
 * =============================================================================
 * PAYMENT FLOW: NEW SUBSCRIPTION
 * =============================================================================
 *
 * 1. User fills form (name, email, card via Stripe CardElement)
 * 2. onFormSubmit():
 *    a. Validates form fields
 *    b. Calls stripe.createPaymentMethod() to tokenize card
 *    c. If user not logged in, calls registerNewUser() first
 * 3. stripePaymentMethodHandler():
 *    a. POST /api/internal/billing_accounts (creates account + subscription)
 *    b. If payment_intent_status === 'requires_action':
 *       - Triggers 3D Secure flow via stripe.confirmCardPayment()
 *       - If 3DS fails, calls vapi.cleanupAccount() to rollback
 *    c. On success, calls vapi.finalizeAccount() to activate subscription
 * 4. reportSuccess(): Shows success modal, then redirects to successUrl
 *
 * =============================================================================
 * PAYMENT FLOW: UPDATE CARD ON FILE
 * =============================================================================
 *
 * When accountCode prop is provided (existing subscription):
 *
 * 1. User enters new card details
 * 2. stripePaymentMethodHandler() calls vapi.updateBillingAccount() instead
 * 3. Updates payment method in Stripe, no new subscription created
 * 4. Redirects immediately (no success modal)
 *
 * =============================================================================
 * 3D SECURE (SCA) HANDLING
 * =============================================================================
 *
 * European cards require Strong Customer Authentication (SCA). When the backend
 * returns payment_intent_status: 'requires_action', we:
 *
 * 1. Call stripe.confirmCardPayment(client_secret) - shows 3DS modal
 * 2. If user cancels or fails: cleanup the partially created account
 * 3. If user succeeds: continue with finalization
 *
 * =============================================================================
 * API ENDPOINTS USED
 * =============================================================================
 *
 * - POST /api/internal/billing_accounts - Create new account (vapi.createBillingAccount)
 * - PUT /api/internal/billing_accounts/:id - Update payment method (vapi.updateBillingAccount)
 * - POST /api/internal/billing_accounts/:id/finalize - Activate subscription (vapi.finalizeAccount)
 * - POST /api/internal/billing_accounts/:id/cleanup - Rollback failed account (vapi.cleanupAccount)
 * - POST /users - Register new user (vapi.signUp) for unauthenticated flow
 *
 * =============================================================================
 * KEY STATE
 * =============================================================================
 *
 * - accountCode: If present, we're updating payment method (not creating new)
 * - isProcessing: Disables form during API calls
 * - isFinished: Shows success modal
 * - fieldErrors: Validation errors for form fields
 * - referral: Rewardful referral tracking for affiliate program
 *
 * @see StripeFormWrapper for the parent component that provides Stripe context
 * @see Api::Internal::BillingAccountsController for the backend API
 */
/* eslint-disable react/forbid-prop-types,react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';
import { ElementsConsumer, CardElement } from '@stripe/react-stripe-js';
import { Spinner } from 'reactstrap';
import _ from 'lodash';
import UpgradeSuccessModal from './_upgrade_success_modal';
import validationHelpers from '../../javascript/frontend/helpers/validation_helpers';
import vapi from '../../javascript/frontend/api/vapi';
import vahoy from '../../javascript/vahoy';
import gaTracking from '../../javascript/ga_tracking';

const unknownMessage = 'Something went wrong, please try again later.';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontFamily: 'Arial, sans-serif',
      color: '#495057',
      fontSmoothing: 'subpixel-antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#FF4074',
      iconColor: '#FF4074',
    },
  },
};

class BillingAccountForm extends React.Component {
  constructor(props) {
    super(props);
    const {
      defaultName,
      defaultCompany,
      defaultEmail,
      accountCode,
    } = this.props;

    const urlParams = new URLSearchParams(window.location.search);
    const promotionCodeFromUrl = urlParams.get('promotion_code') || '';

    this.state = {
      accountCode,
      fields: {
        billingCompany: defaultCompany || '',
        billingName: defaultName,
        billingEmail: defaultEmail,
        promotionCode: promotionCodeFromUrl,
      },
      fieldErrors: {},
      fieldErrorMessages: {},
      generalErrorTitle: '',
      generalErrorMessage: '',
      disableInput: false,
      isProcessing: false,
      formIsEmpty: true,
      isFinished: false,
      referral: '',
    };
  }

  componentDidMount() {
    this.setState({ displayPromoCode: false });
    if (typeof window.rewardful === 'function') {
      window.rewardful('ready', () => {
        this.setState({ referral: window.Rewardful.referral });
      });
    }
  }

  togglePromoCode = () => {
    this.setState((prevState) => ({ displayPromoCode: !prevState.displayPromoCode }));
  };

  validateFields = (eventOrStripeResult = null, apiError = null) => {
    const {
      fields,
      fieldErrors,
    } = this.state;
    if (!fields.billingName) fieldErrors.billingName = 'Please provide a name.';
    if (!validationHelpers.isValidEmail(fields.billingEmail)) fieldErrors.billingEmail = 'Please provide a valid email address.';

    // Special handling for the Stripe card element that does its
    // own validation as the user types
    if (eventOrStripeResult && eventOrStripeResult.error) {
      fieldErrors.paymentCard = eventOrStripeResult.error.message;
    }

    if (apiError && apiError.response && apiError.response.data) {
      fieldErrors.paymentCard = apiError.response.data.message;
    }

    this.setState({ fieldErrors });

    return fieldErrors;
  };

  formIsInvalid = () => {
    // Make a copy of the in-memory field errors into the
    // error messages state value for display
    // Return true if the form is invalid
    const { fieldErrors } = this.state;
    const fieldErrorMessages = _.cloneDeep(fieldErrors);
    this.setState({ fieldErrorMessages });
    return !!Object.keys(fieldErrors).length;
  };

  clearGeneralError = () => {
    this.setState({
      generalErrorTitle: '',
      generalErrorMessage: '',
    });
  };

  disableInput = (disableInput) => {
    this.setState({ disableInput });
  };

  setProcessingState = (isProcessing) => {
    this.setState({ isProcessing }, () => {
      this.disableInput(isProcessing);
    });
  };

  /**
   * Handles successful payment completion.
   *
   * For payment method updates (accountCode present): Redirects immediately.
   * For new subscriptions: Shows success modal first, then redirects on dismiss.
   */
  reportSuccess = () => {
    const { accountCode } = this.state;

    this.setProcessingState(false);
    this.disableInput(true);

    // Presence of accountCode indicates we did a payment method update versus
    // creating a new billing account. If we just updated the payment, there's no
    // need to do isFinished, which displays the success modal. Instead, just immediately
    // do the onSuccess callback and move the the successURL
    if (accountCode) {
      const {
        onSuccess,
        successUrl,
      } = this.props;
      onSuccess();
      window.location.replace(successUrl);
    } else {
      this.setState({ isFinished: true });
    }
  };

  displayGeneralError = (message, title = null) => {
    const generalErrorTitle = title || 'Oops!';
    const generalErrorMessage = message;
    this.setState({
      generalErrorTitle,
      generalErrorMessage,
    });
    this.setProcessingState(false);

    const { pageName } = this.props;
    gaTracking.trackSignUp(pageName, 'failStripePayment', 'generalError', generalErrorTitle, message);
    vahoy.track('NewBillingAccount#failStripePaymentWithGeneralError');
  };

  displayStripeError = (result = null, apiError = null) => {
    const fieldErrors = this.validateFields(result, apiError) || {};
    const { pageName } = this.props;
    this.formIsInvalid();
    this.setProcessingState(false);

    gaTracking.trackSignUp(pageName, 'failStripePayment', 'stripeError', JSON.stringify(fieldErrors));
    vahoy.track('NewBillingAccount#failStripePaymentWithStripeError');
  };

  displayServerValidationErrors = (errors) => {
    const messages = [];

    // eslint-disable-next-line guard-for-in
    for (const key in errors) {
      const values = errors[key];

      if (values) {
        messages.push(`${_.startCase(key)} ${values.join(', ')}`);
      }
    }

    this.displayGeneralError(messages.join('. '));
  };

  onSuccessDialogDismiss = () => {
    const {
      onSuccess,
      successUrl,
      pageName,
    } = this.props;
    this.setState({ isFinished: false });
    onSuccess();
    gaTracking.trackSignUp(pageName, 'closeSuccessDialog');
    vahoy.track('NewBillingAccount#closeSuccessDialog');
    window.location.replace(successUrl);
  };

  onInputChange = (e) => {
    const {
      fields,
      fieldErrors,
      fieldErrorMessages,
    } = this.state;
    const formIsEmpty = false;
    let fieldName;
    let fieldValue;
    let eventOrStripeResult;

    // The Stripe card element doesn't conform to standard
    // inputs, so normalize the name and value here
    if (e.target) {
      eventOrStripeResult = {};
      fieldName = e.target.name;
      fieldValue = e.target.value;
    } else {
      fieldName = 'paymentCard';
      fieldValue = undefined; // Value is unused for Stripe card element
      eventOrStripeResult = e;
    }

    // Set the field value
    fields[fieldName] = fieldValue;

    // Clear any errors for the field both for errors (in memory)
    // and the messages (those displayed)
    delete fieldErrors[fieldName];
    delete fieldErrorMessages[fieldName];

    // Put it all into state
    this.setState({
      fields,
      fieldErrors,
      fieldErrorMessages,
      formIsEmpty,
    }, () => {
      // Run field-level validations once state is updated
      this.validateFields(eventOrStripeResult);
    });
  };

  applyPromotionCode = (e) => {
    e.preventDefault();
    const { fields } = this.state;
    const promotionCode = fields.promotionCode.trim();

    if (promotionCode) {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('promotion_code', promotionCode);

      window.location.href = currentUrl.toString();
    }
  };

  /**
   * Creates a new user account for unauthenticated checkout.
   *
   * Used when a user signs up and pays in a single flow (new_unauthenticated).
   * Creates the user first, then proceeds with billing account creation.
   *
   * @param {string} email - User's email address
   * @param {string} name - Full name (split into first/last)
   * @param {string} sourceSubtype - User type (e.g., 'source_smb')
   * @returns {Promise} API response from user registration
   */
  registerNewUser = async (email, name, sourceSubtype) => {
    const nameArray = name.split(/\s+/);
    const firstName = nameArray.shift();
    const lastName = nameArray.join(' ');

    const registerParams = {
      user: {
        email,
        first_name: firstName,
        last_name: lastName,
        legal_agreement: '1',
        offer_free_trial: true,
        user_type: 'source',
        source_subtype: sourceSubtype || 'source_smb',
      },
    };

    return vapi.signUp(registerParams);
  };

  /**
   * CRITICAL: Main payment processing handler after card tokenization.
   *
   * This method orchestrates the server-side payment flow:
   *
   * 1. Sends payment method to backend (create or update billing account)
   * 2. Handles 3D Secure if required (payment_intent_status: 'requires_action')
   * 3. Finalizes the subscription on success
   * 4. Cleans up partial accounts on failure
   *
   * Error handling is critical here - we must cleanup partial accounts
   * if payment fails mid-flow to avoid orphaned Stripe records.
   *
   * @param {Object} result - Result from stripe.createPaymentMethod()
   * @param {string} billingCompany - Optional company name for billing
   */
  stripePaymentMethodHandler = async (result, billingCompany) => {
    const billingAccountParams = {};
    const {
      stripe,
      sourcePlan,
      delegateUserId,
      promotionCodeId,
      pageName,
    } = this.props;
    const { accountCode, referral } = this.state;
    let apiResult;

    if (result.error) {
      this.displayStripeError(result);
    } else {
      billingAccountParams.payment_method = result.paymentMethod.id;
      billingAccountParams.name = result.paymentMethod.billing_details.name;
      billingAccountParams.email = result.paymentMethod.billing_details.email;
      billingAccountParams.company = billingCompany;
      billingAccountParams.source_plan = sourcePlan;
      billingAccountParams.promotion_code_id = promotionCodeId;
      billingAccountParams.referral = referral;

      try {
        if (accountCode) {
          apiResult = await vapi.updateBillingAccount(accountCode, billingAccountParams);
        } else {
          apiResult = await vapi.createBillingAccount(delegateUserId, billingAccountParams);
        }
        const { subscription } = apiResult.data;

        if (subscription) {
          if (subscription.payment_intent_status === 'requires_action') {
            // Perform 3D secure validation
            const confirmResult = await stripe.confirmCardPayment(subscription.client_secret);

            if (confirmResult.error) {
              if (subscription.account_code) {
                await vapi.cleanupAccount(subscription.account_code);
              }
              this.displayStripeError(confirmResult);
              return;
            }
          }
          // Success! Trigger the last steps on the server if necessary
          if (!accountCode) {
            // (Only need to do this last step when we're creating a new billing account)
            // The web hooks for invoices (finalized and paid) act as our insurance policy:
            // The finalizeAccount method call here will activate the premium Qwoted account
            // in a synchronous fashion so the user has immediate access.
            // But if something happens in the browser and this doesn't get called, the events
            // Stripe sends via web hook to our server will take care of everything.
            await vapi.finalizeAccount(subscription.account_code);
          }
          this.reportSuccess();
          gaTracking.trackSignUp(pageName, 'succeedStripePayment', billingAccountParams.name, billingAccountParams.source_plan);
          vahoy.track('NewBillingAccount#succeedStripePayment');
        } else {
          const { errors } = apiResult.data;
          let errorMessage = unknownMessage;

          if (errors) {
            errorMessage = errors.join('. ');
          }

          this.displayGeneralError(errorMessage);
        }
      } catch (error) {
        if (error.response && error.response.status === 402) {
          // Card error
          if (error.response.data && error.response.data.account_code) {
            await vapi.cleanupAccount(error.response.data.account_code);
          }
          this.displayStripeError(null, error);
        } else {
          let message;

          if (error.response && error.response.data) {
            if (error.response.data.account_code) {
              await vapi.cleanupAccount(error.response.data.account_code);
            }
            const responseData = error.response.data;
            message = responseData.error || responseData.message || unknownMessage;
          } else {
            message = unknownMessage;
          }
          this.displayGeneralError(message);
        }
      }
    }
  };

  /**
   * Form submission handler - entry point for payment flow.
   *
   * Three paths depending on authentication state:
   *
   * 1. Logged-in user: Tokenize card → stripePaymentMethodHandler
   * 2. Guest checkout: Tokenize card → registerNewUser → stripePaymentMethodHandler
   * 3. Invalid form: Show validation errors, abort
   *
   * The stripe.createPaymentMethod() call tokenizes the card securely without
   * sending raw card data to our servers (PCI compliance).
   */
  onFormSubmit = async (e) => {
    e.preventDefault();
    this.clearGeneralError();
    this.setProcessingState(true);

    const {
      currentUserId,
      elements,
      pageName,
      sourceSubtype,
      stripe,
    } = this.props;
    const {
      billingName,
      billingCompany,
      billingEmail,
    } = this.state.fields;

    if (this.formIsInvalid() || !stripe || !elements) {
      // Don't continue if fields are invalid or Stripe isn't loaded
      this.setProcessingState(false);
      gaTracking.trackSignUp(pageName, 'failSubmitForm', 'UserErrorOrPageError', `InvalidForm: ${this.formIsInvalid}, stripeNotFound: ${!stripe}, elementsNotFound: ${!elements}`);
      vahoy.track('NewBillingAccount#failSubmitForm');
      return;
    }

    const result = await stripe.createPaymentMethod({
      type: 'card',
      card: elements.getElement(CardElement),
      billing_details: {
        name: billingName,
        email: billingEmail,
      },
    });

    if (result.error) {
      // Initial Stripe call to createPaymentMethod failed
      this.displayStripeError(result);
    } else if (currentUserId) {
      // createPaymentMethod succeeded and we are logged in ...
      this.stripePaymentMethodHandler(result, billingCompany);
    } else {
      // sign_up_for_free_trial: createPaymentMethod succeeded and we are NOT logged in so now we must
      // register a new user, do the rest of the Stripe process
      this.registerNewUser(billingEmail, billingName, sourceSubtype)
        .then(() => {
          this.stripePaymentMethodHandler(result, billingCompany);
        })
        .catch((errorResponse) => {
          const responseData = errorResponse.response && errorResponse.response.data;
          const errors = responseData && responseData.errors;
          if (errors) {
            this.displayServerValidationErrors(errors);
          } else {
            const message = responseData.error || responseData.message || unknownMessage;
            this.displayGeneralError(message);
          }
        });
    }

    vahoy.track('NewBillingAccount#submitForm');
  };

  render() {
    const {
      stripe,
      priceString,
      planNameString,
      planInterval,
      cancelPath,
      freeTrial,
      hideLinksForCcPage,
    } = this.props;

    const {
      fields,
      fieldErrorMessages,
      formIsEmpty,
      generalErrorTitle,
      generalErrorMessage,
      disableInput,
      isProcessing,
      isFinished,
      accountCode,
      referral,
      displayPromoCode,
    } = this.state;

    const errorBox = generalErrorMessage ? (
      <div className="alert alert-danger mb-3" role="alert">
        <h4 className="alert-heading">{generalErrorTitle}</h4>
        <p>{generalErrorMessage}</p>
        <hr />
        <small className="mb-0">
          If you continue to experience problems, please contact us at {window.SUPPORT_EMAIL}.
        </small>
      </div>
    ) : '';

    const zeroPayment = priceString === '$0.00';

    let processingText;

    // Set the button text based on processing status and presence of accountCode:
    // When present, we're doing a card on file update. When absent, we're doing
    // a payment and creation of a billing account.
    if (accountCode) {
      processingText = isProcessing ? 'Updating Payment Method...' : 'Update Payment Method';
    } else {
      let payString = `Upgrade to ${planNameString} - ${priceString}`;
      if (freeTrial) {
        payString = 'Start Trial';
      }

      if (zeroPayment) {
        processingText = isProcessing ? 'Validating Card...' : payString;
      } else {
        processingText = isProcessing ? 'Processing Payment...' : payString;
      }
    }

    const processingButton = (
      <button
        type="submit"
        disabled={!stripe || formIsEmpty || disableInput}
        className="btn btn-lg btn-primary fs-5 rounded-1 p-0 w-100 stripe-submit mt-3"
        style={{
          height: 'auto',
          backgroundImage: 'linear-gradient(to right, #5144B6, #8D80EB)',
        }}
      >
        <div className="d-flex justify-content-end">
          <Spinner
            color="light"
            className="m-3"
            style={{
              width: '1.5rem',
              height: '1.5rem',
              display: `${isProcessing ? 'inline-block' : 'none'}`,
            }}
          />
          <span
            className="w-100 font-size-16px fw-bold flex-column text-align-right ms-2"
            style={{ marginTop: '20px' }}
          >
            {processingText}
          </span>
          <span className="float-end p-3 rounded rounded-1 bg-primary">
            <i className="fa fa-arrow-right text-white fs-2" />
          </span>
        </div>
      </button>
    );

    const onCancel = () => {
      const { pageName } = this.props;
      gaTracking.trackSignUp(pageName, 'cancelStripePayment');
      vahoy.track('NewBillingAccount#clickCancel');
      window.location.replace(cancelPath);
    };

    const cancelButton = (
      <button
        type="button"
        disabled={disableInput}
        className="btn text-primary btn-large cc-cancel-button rounded-5 py-2 me-3 shadow bg-white"
        onClick={onCancel}
      >
        Cancel
      </button>
    );

    return (
      <div className="billing-account-form">
        <UpgradeSuccessModal
          showPopup={isFinished}
          onCancel={this.onSuccessDialogDismiss}
          zeroPayment={zeroPayment}
        />
        {errorBox}
        <div className="row">
          <div className="col-12">
            <form
              onSubmit={this.onFormSubmit}
              data-rewardful={true}
            >
              {referral ? <input type="hidden" name="referral" value={referral} /> : null}

              <div className="row">
                <div className="col-md-12 mb-2 required">
                  <label
                    className="form-label text-muted"
                    htmlFor="billing_name"
                  >
                    Name on card
                  </label>
                  <input
                    className={`form-control bg-white border-1 border billing-input ${fieldErrorMessages.billingName ? 'is-invalid' : ''}`}
                    placeholder="Name of billing contact"
                    name="billingName"
                    id="billing_name"
                    value={fields.billingName}
                    disabled={disableInput}
                    onChange={this.onInputChange}
                    style={{ background: '#FFF' }}
                  />
                  <small className="form-text d-block text-danger">
                    {fieldErrorMessages.billingName}
                  </small>
                </div>
              </div>

              <div className="d-flex justify-content-between flex-column flex-md-row">
                <div className="mb-2 flex-grow-1 me-2">
                  <label
                    className="form-label text-muted"
                    htmlFor="billing_company"
                  >
                    Company name
                  </label>
                  <input
                    className="form-control py-1 border-1 border billing-input"
                    placeholder="Optional company name"
                    name="billingCompany"
                    id="billing_company"
                    value={fields.billingCompany}
                    disabled={disableInput}
                    onChange={this.onInputChange}
                    style={{ background: '#FFF' }}
                  />
                </div>

                <div className="mb-2 required flex-grow-1 ms-2">
                  <label
                    className="form-label text-muted"
                    htmlFor="billing_email"
                  >
                    Email
                  </label>
                  <input
                    className={`form-control bg-white border-1 border billing-input ${fieldErrorMessages.billingEmail ? 'is-invalid' : ''}`}
                    placeholder="Email address for invoices"
                    name="billingEmail"
                    id="billing_email"
                    value={fields.billingEmail}
                    disabled={disableInput}
                    onChange={this.onInputChange}
                    style={{ background: '#FFF' }}
                  />
                  <small className="form-text text-danger">
                    {fieldErrorMessages.billingEmail}
                  </small>
                </div>
              </div>

              <div
                className="mb-2 required"
                style={{ visibility: disableInput ? 'hidden' : 'visible' }}
              >
                <label htmlFor="card-element" className="form-label text-muted">
                  Card information
                </label>
                <div className="pb-2">
                  <div id="card-element">
                    <CardElement
                      className={`form-control border-1 border bg-white ${fieldErrorMessages.paymentCard ? 'is-invalid' : ''}`}
                      options={CARD_ELEMENT_OPTIONS}
                      disabled={disableInput}
                      onChange={this.onInputChange}
                    />
                  </div>
                  <small className="form-text text-danger">
                    {fieldErrorMessages.paymentCard}
                  </small>
                </div>
              </div>
              <div className="row gx-0 justify-content-start mb-2 border-top border-dark border-2 mt-2 pt-2">
                <div className="col-md-4 mb-2">
                  <label>
                    <button
                      className="form-label text-primary fw-bold"
                      style={{ cursor: 'pointer', border: 0, background: 'none' }}
                      htmlFor="promotion_code"
                      onClick={this.togglePromoCode}
                      type="button"
                    >
                      Have a promotion code?
                    </button>
                  </label>
                  { displayPromoCode && (
                    <div className="input-group">
                      <input
                        className="focus-outline-hidden form-control bg-white border-1 border"
                        placeholder="Enter promotion code"
                        name="promotionCode"
                        id="promotion_code"
                        value={fields.promotionCode}
                        disabled={disableInput}
                        onChange={this.onInputChange}
                        style={{ background: '#FFF', cursor: 'pointer' }}
                      />
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={this.applyPromotionCode}
                        style={{ cursor: 'pointer' }}
                        disabled={disableInput || !fields.promotionCode.trim()}
                      >
                        Apply
                      </button>
                    </div>
                   )}
                </div>
                <div className="col-md-2" />
                <div className="col pe-3 fs-4 fw-bold">
                  <div className="d-flex justify-content-between">
                    <span className="total"> Due today </span>
                    <span className="amount">
                      {freeTrial == true
                        && (
                          <strong>
                            0.00
                          </strong>
                        )}

                      {freeTrial == false
                        && (
                          <strong>
                            {priceString}
                          </strong>
                        )}
                    </span>
                  </div>
                  <small
                    className="font-size-10px"
                    style={{
                      display: 'block',
                      lineHeight: '1.375',
                    }}
                  >
                    {freeTrial == true
                      && (
                        <span>
                          Automatic payment of {priceString} after 2 weeks.
                        </span>
                      )}

                    {freeTrial == false
                      && (
                        <span>
                          Next automatic payment in 1 {planInterval}.
                        </span>
                      )}
                  </small>
                </div>
              </div>
              <div className="row">
                <div className="col">
                  {hideLinksForCcPage == false
                    && (
                     <span className="ms-3">{ cancelButton }</span>
                  )}
                  {processingButton}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }
}

BillingAccountForm.propTypes = {
  accountCode: PropTypes.number, // Billing account id provided when updating payment method instead of creating account
  cancelPath: PropTypes.string.isRequired, // Where to navigate to when the user clicks the cancel link
  currentUserId: PropTypes.number,
  defaultCompany: PropTypes.string, // Existing company name on billing account used when updating payment method
  defaultEmail: PropTypes.string.isRequired, // Email to pre-populate in the billing email field
  defaultName: PropTypes.string.isRequired, // Name to pre-populate in the billing name field
  delegateUserId: PropTypes.number, // If current user is an admin, send in the user id to attach the new billing account to
  elements: PropTypes.object, // Stripe Elements instance to be injected by ElementsConsumer
  freeTrial: PropTypes.bool,
  hideLinksForCcPage: PropTypes.bool,
  onSuccess: PropTypes.func.isRequired, // Function to call when payment is successful
  pageName: PropTypes.string,
  planInterval: PropTypes.string, // 'month' or 'year' depending on the billing plan interval
  planNameString: PropTypes.string, // String representation of the billing plan name to display on form and submit button
  priceString: PropTypes.string, // String representation of price to charge immediately
  promotionCodeId: PropTypes.string, // If there is an applied promotion, the Stripe ID for that promotion
  sourcePlan: PropTypes.string, // Stripe ID for the plan used for subscription
  sourceSubtype: PropTypes.string,
  stripe: PropTypes.object, // Stripe object from loadStripe method to be injected by ElementsConsumer
  successUrl: PropTypes.string.isRequired, // URL to redirect to when the payment is successful
};

export default function InjectedCheckoutForm(props) {
  const {
    accountCode,
    onboardedUser,
    cancelPath,
    currentUserId,
    defaultCompany,
    defaultEmail,
    defaultName,
    delegateUserId,
    freeTrial,
    onSuccess,
    pageName,
    planInterval,
    planNameString,
    priceString,
    promotionCodeId,
    sourceSubtype,
    sourcePlan,
    successUrl,
  } = props;

  return (
    <ElementsConsumer>
      {({
          elements,
          stripe,
        }) => (
        <BillingAccountForm
          accountCode={accountCode}
          cancelPath={cancelPath}
          currentUserId={currentUserId}
          defaultCompany={defaultCompany}
          defaultEmail={defaultEmail}
          defaultName={defaultName}
          delegateUserId={delegateUserId}
          elements={elements}
          freeTrial={freeTrial}
          onSuccess={onSuccess}
          onboardedUser={onboardedUser}
          pageName={pageName}
          planInterval={planInterval}
          planNameString={planNameString}
          priceString={priceString}
          promotionCodeId={promotionCodeId}
          sourcePlan={sourcePlan}
          sourceSubtype={sourceSubtype}
          stripe={stripe}
          successUrl={successUrl}
        />
      )}
    </ElementsConsumer>
  );
}

InjectedCheckoutForm.propTypes = {
  accountCode: PropTypes.number, // Billing account id provided when updating payment method instead of creating account
  cancelPath: PropTypes.string.isRequired, // Where to navigate to when the user clicks the cancel link
  currentUserId: PropTypes.number,
  defaultCompany: PropTypes.string, // Existing company name on billing account used when updating payment method
  defaultEmail: PropTypes.string.isRequired, // Email to pre-populate in the billing email field
  defaultName: PropTypes.string.isRequired, // Name to pre-populate in the billing name field
  delegateUserId: PropTypes.number, // If current user is an admin, send in the user id to attach the new billing account to
  freeTrial: PropTypes.bool,
  onSuccess: PropTypes.func.isRequired, // Function to call when payment is successful
  onboardedUser: PropTypes.bool.isRequired, // Indicates whether or not the signed in user is already onboarded
  pageName: PropTypes.string,
  planInterval: PropTypes.string, // 'month' or 'year' depending on the billing plan interval
  planNameString: PropTypes.string, // String representation of the billing plan name to display on form and submit button
  priceString: PropTypes.string, // String representation of price to charge immediately
  promotionCodeId: PropTypes.string, // If there is an applied promotion, the Stripe ID for that promotion
  sourcePlan: PropTypes.string, // Stripe ID for the plan used for subscription
  sourceSubtype: PropTypes.string,
  successUrl: PropTypes.string.isRequired, // URL to redirect to when the payment is successful
};
